<template>
  <div :title="`${action_name} ${module_name}`" button-accept="false" button-cancel="false" :active.sync="isActive">
    <div class="updates-container rounded-xl p-4" style="max-height: 100vh; border-radius: 20px !important;">
      <vs-textarea v-model="form.update_message" placeholder="Type your update here..." class="w-full mb-4 updates-textarea" rows="15" style="border-radius: 10px" />

      <!-- Display selected files preview -->
      <div v-if="selectedFiles || existingMedia" class="mb-4">
        <div class="flex items-center mb-4">
          <div class="relative inline-block m-2">
            <img v-if="isImage(selectedFiles || existingMedia)" :src="getFileUrl(selectedFiles || existingMedia)" class="h-48 rounded" />
            <video v-else :src="getFileUrl(selectedFiles || existingMedia)" class="h-48 rounded" controls></video>
          </div>

          <vx-tooltip :text="`Remove Image`">
            <vs-button @click="removeFile()" color="danger" type="gradient" icon-pack="feather" icon="icon-trash-2" />
          </vx-tooltip>
        </div>
      </div>

      <div class="flex items-center justify-between mb-4 gap-2">
        <div class="w-full">
          <vs-button color="dark" type="flat" icon="image" class="w-full" @click="$refs.fileInput.click()"> Add Image/Video </vs-button>
          <input type="file" ref="fileInput" class="hidden" @change="handleFileUpload" accept="image/*,video/*" />
        </div>
        <div>
          <vs-button v-if="selectedRecordId" color="danger" @click="cancelEdit" :disabled="createLoading" class="w-full rounded-xl update-cancel-button"> Cancel </vs-button>
        </div>
        <vs-button
          class="w-full update-form-button"
          @click="save_changes"
          :disabled="!form.update_message || createLoading"
          :loading="createLoading"
          id="action-button"
          :style="!form.update_message || createLoading ? 'background-color: #000; opacity: 1; cursor: not-allowed;' : ''"
        >
          {{ selectedRecordId ? 'Update' : 'Post' }}
        </vs-button>
      </div>
    </div>
  </div>
</template>

<script>
import { mapActions, mapGetters, mapState } from 'vuex'
import Select2 from '@/components/custom/form-elements/Select2.vue'
export default {
  /** Component Name */
  name: 'AddUpdateUpdate',

  /** Props */
  props: {
    selectedRecordId: String,
    module_name: String,
    action_name: String,
    showModal: Boolean
  },

  components: {
    Select2
  },

  /** Data */
  data() {
    return {
      form: {
        title: '',
        update_message: '',
        update_media: []
      },
      existingMedia: null,
      selectedFiles: null,
      isRemoveFile: false
    }
  },

  /** Computed Properties */
  computed: {
    ...mapGetters('auth', ['checkPermissionSlug']),
    ...mapState('gurudwara', ['createLoading']),
    validateForm() {
      return !this.errors.any()
    },
    isActive: {
      get() {
        return this.showModal
      },
      set(val) {
        this.$emit('update:showModal', val)
      }
    }
  },

  /** Methods */
  methods: {
    cancelEdit() {
      this.isActive = false
      this.resetForm()
      this.$emit('cancel-edit')
      this.selectedRecordId = null
    },

    setEditData(id) {
      this.selectedRecordId = id
      // Reset form first
      this.resetForm()

      // Fetch update data
      this.$store
        .dispatch('gurudwara/updateShow', id)
        .then((res) => {
          const data = res.data.data
          this.form.update_message = data.update_message

          // Handle media files - adjust based on your API response
          if (data.update_media && data.update_media.length) {
            this.existingMedia = data.update_media[0] // Store first media only
          }
        })
        .catch((error) => {
          this.$vs.notify({
            title: 'Error',
            text: error.message || 'Failed to load update',
            color: 'danger',
            position: 'top-center'
          })
        })
    },

    ...mapActions('gurudwara', {
      updateCreateOrUpdate: 'updateCreateOrUpdate'
    }),
    isImage(file) {
      if (!file) return false

      // Handle existing media object from API
      if (file.media_type) {
        return file.media_type.startsWith('image/')
      }

      // Handle newly uploaded File object
      if (file.type) {
        return file.type.startsWith('image/')
      }

      // Handle case where file might have url (for existing media)
      if (file.url) {
        // Check file extension as fallback
        const url = file.url.toLowerCase()
        return url.match(/\.(jpeg|jpg|gif|png|svg|webp|bmp)$/) != null
      }

      return false
    },

    getFileUrl(file) {
      if (!file) return ''

      // If file is from API (existing media)
      if (file.s3_url) {
        return file.s3_url
      }

      // If file is a newly uploaded File object
      if (file instanceof File || file instanceof Blob) {
        return URL.createObjectURL(file)
      }

      // If file has a direct url property
      if (file.url) {
        return file.url
      }

      return ''
    },

    handleFileUpload(e) {
      const files = e.target.files
      if (!files.length) return
      this.selectedFiles = null
      this.selectedFiles = files[0]
      // for (let i = 0; i < files.length; i++) {
      //   this.selectedFiles.push(files[i])
      // }
      e.target.value = '' // Reset input\
      this.isRemoveFile = false
    },

    removeFile() {
      this.selectedFiles = null
      this.existingMedia = null
      this.isRemoveFile = true
    },

    async save_changes() {
      const formData = new FormData()

      // Add form data
      formData.append('update_message', this.form.update_message)
      formData.append('gurudwaraId', this.$route.params.id)

      // Add media files
      // this.selectedFiles.forEach(file => {
      //   formData.append('update_media', file)
      // })

      if (this.selectedFiles) {
        formData.append('update_media', this.selectedFiles)
      }

      // Add update ID if editing
      if (this.selectedRecordId) {
        formData.append('gurudwaraUpdateId', this.selectedRecordId)
      }
      formData.append('isRemoveFile', this.isRemoveFile)

      try {
        await this.updateCreateOrUpdate({
          id: this.selectedRecordId,
          data: formData,
          headers: {
            'Content-Type': 'multipart/form-data'
          }
        })

        this.$vs.notify({
          title: 'Success',
          text: `Update ${this.selectedRecordId ? 'updated' : 'created'} successfully`,
          color: 'success',
          position: 'top-center'
        })

        this.isActive = false
        this.$emit('update-data') // Refresh parent list
        this.resetForm()

        this.$emit('update-submitted')
      } catch (error) {
        this.$vs.notify({
          title: 'Error',
          text: error.message || 'Something went wrong',
          color: 'danger',
          position: 'top-center'
        })
      }
    },

    resetForm() {
      this.form.update_message = ''
      this.selectedFiles = null
      this.existingMedia = null
    }
  },

  watch: {
    createLoading: {
      handler(newVal) {
        if (newVal) {
          this.$vs.loading({
            container: '#action-button',
            scale: 0.45
          })
        } else {
          this.$vs.loading.close('#action-button > .con-vs-loading')
        }
      },
      immediate: true
    }
  }
}
</script>
